import cx from 'classnames'
import {
  type ComponentPropsWithRef,
  type MouseEvent,
  type ReactNode,
  useCallback,
} from 'react'
import { match } from '@/utils/pattern-match-util'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import {
  type AlignmentProps,
  type CellProps,
  CheckCellContent,
  type CheckCellProps,
  type ColumnDensity,
  type TestIdProps,
  DateDisplay,
  type FormatDate,
  ImageCellContent,
  type ImageCellProps,
  TextCellContent,
} from './TableCell'
import { Checkbox } from '@/components/Checkbox'
import styles from './Table.module.css'

interface TableHostProps {
  children?: ReactNode
}

/**
 * Table Host, adding border and stretching + filling layout so that
 * the table is stretched across the available horizontal space and fills
 * the available vertical space. The parent must be configured as a flex
 * container for those effects.
 */
export function TableHost({ children }: TableHostProps) {
  return <div className={styles.host}>{children}</div>
}

interface TableProps extends ComponentPropsWithRef<'table'> {
  striped?: boolean
  stickyHeader?: boolean
}

/**
 * A table, optionally striped and with a sticky header.
 */
export function Table({
  className,
  striped = false,
  stickyHeader = false,
  ref,
  ...rest
}: TableProps) {
  return (
    <table
      ref={ref}
      className={cx(
        styles.base,
        striped && styles.striped,
        stickyHeader && styles.stickyHeader,
        className,
      )}
      {...rest}
    ></table>
  )
}

type TableHeadProps = ComponentPropsWithRef<'thead'>

/**
 * The table's header row.
 */
export function TableHead({
  className,
  children,
  ref,
  ...rest
}: TableHeadProps) {
  return (
    <thead ref={ref} className={cx(styles.base, className)} {...rest}>
      <tr>{children}</tr>
    </thead>
  )
}

/**
 * The sort order a {@link TableHeader} shows.
 */
export type TableSortOrder = 'asc' | 'desc' | 'none'

interface TableHeaderProps extends ComponentPropsWithRef<'th'>, AlignmentProps {
  /**
   * If a TableHeader `canSort` then it will display a sort indicator and
   * make the header clickable
   */
  canSort?: boolean

  /**
   * sets the alignment for the th element, it could be left, right or center
   */
  align?: 'left' | 'right' | 'center'

  /**
   * If a sort order is specified, a sort order indicator will be displayed
   * This will replace the default hasOrder indicator
   */
  sortOrder?: TableSortOrder

  /**
   * A callback that will be called with the next sortOrder when
   * this table header is clicked
   */
  onSort?: (sortOrder: TableSortOrder) => void
}

/**
 * A column header, which can sort the table.
 */
export function TableHeader({
  className,
  children,
  canSort = false,
  sortOrder = 'asc',
  onSort = () => null,
  alignment,
  ref,
  ...rest
}: TableHeaderProps) {
  return (
    <th
      ref={ref}
      className={cx(className, alignment && styles[alignment])}
      {...rest}
    >
      {canSort ? (
        <RawButton
          className={styles.headerWithSort}
          onPress={() =>
            onSort(match(sortOrder, { none: 'asc', asc: 'desc', desc: 'none' }))
          }
        >
          {children}
          <span
            className={cx(
              styles.sortIcon,
              sortOrder !== 'none' && styles.sortIconActive,
            )}
          >
            {sortOrder === 'none' ? (
              <Icon size={20} name="dropdown" />
            ) : (
              <Icon
                size={12}
                name="sorting"
                rotate={sortOrder === 'asc' ? -180 : 0}
              />
            )}
          </span>
        </RawButton>
      ) : (
        <span>{children}</span>
      )}
    </th>
  )
}

type TableBodyProps = ComponentPropsWithRef<'tbody'>

/**
 * The table's body.
 */
export function TableBody({ className, ref, ...rest }: TableBodyProps) {
  return <tbody ref={ref} className={className} {...rest}></tbody>
}

interface TableRowProps extends ComponentPropsWithRef<'tr'> {
  odd?: boolean
}

/**
 * A table row, styled as clickable when it has an `onClick`.
 */
export function TableRow({
  className,
  children,
  odd,
  onClick,
  ref,
  ...rest
}: TableRowProps) {
  return (
    <tr
      ref={ref}
      className={cx(
        styles.row,
        odd && styles.odd,
        className,
        onClick && styles.clickable,
      )}
      onClick={onClick}
      {...rest}
    >
      {children}
    </tr>
  )
}

interface TableCellProps extends Omit<
  ComponentPropsWithRef<'td'>,
  'align' | 'width'
> {
  align?: 'left' | 'right' | 'center'
  width?: number
  testId?: string
}

/**
 * A table cell.
 */
export function TableCell({
  className,
  children,
  width,
  align = 'left',
  style,
  testId,
  ref,
  ...rest
}: TableCellProps) {
  return (
    <td
      data-test={testId}
      ref={ref}
      align={align}
      className={cx(styles.row, className)}
      style={{
        ...(width ? { width } : undefined),
        ...style,
      }}
      {...rest}
    >
      {children}
    </td>
  )
}

/**
 * A cell of text.
 */
export function TextCell({
  children,
  alignment,
  testId,
}: CellProps & AlignmentProps & TestIdProps) {
  return (
    <TableCell className={cx(styles.textCell, alignment && styles[alignment])}>
      <TextCellContent testId={testId}>{children}</TextCellContent>
    </TableCell>
  )
}

interface DateCellProps {
  date: Date | null
  density?: ColumnDensity
  format?: FormatDate
}

/**
 * A cell showing a date.
 */
export function DateCell({
  date,
  density,
  format,
  testId,
}: DateCellProps & TestIdProps) {
  return (
    <TextCell testId={testId}>
      {date && (
        <DateDisplay
          date={date}
          format={format}
          density={density ?? 'Default'}
        />
      )}
    </TextCell>
  )
}

/**
 * A cell showing an avatar and a title.
 */
export function ImageCell({
  title,
  image,
  imageFallback,
  size,
  minWidth,
  noPadding,
  testId,
}: ImageCellProps) {
  return (
    <TableCell testId={testId}>
      <ImageCellContent
        title={title}
        image={image}
        size={size}
        imageFallback={imageFallback}
        minWidth={minWidth}
        noPadding={noPadding}
      />
    </TableCell>
  )
}

/**
 * A cell showing a check mark when checked.
 */
export function CheckCell({ checked, color }: CheckCellProps) {
  return (
    <TableCell>
      <CheckCellContent checked={checked} color={color} />
    </TableCell>
  )
}

interface CheckboxCellProps {
  checked?: boolean | 'indeterminate'
  onChange?(checked: boolean): unknown
}

/**
 * A cell with a checkbox; dragging over several with the mouse held toggles each.
 */
export function CheckboxCell({
  checked,
  onChange,
  testId,
}: CheckboxCellProps & TestIdProps) {
  const change = useCallback(() => {
    onChange?.(checked === 'indeterminate' ? true : !checked)
  }, [onChange, checked])

  // Allows multiple checkboxes to be checked by dragging the mouse
  const handleMouseEvents = (e: MouseEvent) => {
    if (e.buttons === 1) {
      change()
    }
  }

  return (
    <TableCell onMouseEnter={handleMouseEvents} onMouseDown={handleMouseEvents}>
      <Checkbox checked={checked} muteClickEvent testId={testId} />
    </TableCell>
  )
}
